import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { BarChart3, FileText, LogOut, Users } from 'lucide-react';
import Sidebar from './components/Sidebar';
import ProducaoBPA from './components/ProducaoBPA';
import GeraBPA from './components/GeraBPA';
import PacientesIncompletos from './components/PacientesIncompletos';
import Usuarios from './components/Usuarios';
import Transport from './components/Transport';
import TransportReports from './components/transport/TransportReports';
import BpaTransporte from './components/BpaTransporte';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import APP_CONFIG, { getAppTitle } from './config/appConfig';
import { getInitials } from './components/ui/styles';
import { PageHeader, PageShell, Panel } from './components/ui/PageShell';
import DocumentTitle from './components/DocumentTitle';
import PainelChamadas from './components/PainelChamadas';
import './App.css';

const Dashboard = () => {
  const { user, logout } = useAuth();

  const cards = [
    {
      icon: FileText,
      title: 'Boletim',
      description: 'Gerencie os boletins e relatórios do BPA',
      tone: 'bg-primary-light text-primary',
    },
    {
      icon: Users,
      title: 'Usuários',
      description: 'Gerencie usuários e permissões do sistema',
      tone: 'bg-emerald-50 text-emerald-600',
    },
    {
      icon: BarChart3,
      title: 'Relatórios',
      description: 'Visualize relatórios e métricas consolidadas',
      tone: 'bg-amber-50 text-amber-600',
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title={getAppTitle()}
        description={APP_CONFIG.DESCRIPTION}
        user={{
          name: user?.name || user?.username || 'Usuário',
          role: user?.role === 'admin' ? 'Administrador' : 'Usuário',
          initials: getInitials(user?.name || user?.username),
        }}
        actions={(
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium text-text transition hover:bg-slate-50"
          >
            <LogOut size={16} />
            Sair
          </button>
        )}
      />

      <div className="grid grid-cols-1 gap-5 px-6 py-6 lg:grid-cols-3 lg:px-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Panel key={card.title}>
              <div className="p-5">
                <div className={`mb-4 flex h-10 w-10 items-center justify-center rounded-xl ${card.tone}`}>
                  <Icon size={18} />
                </div>
                <h3 className="text-lg font-semibold text-text">{card.title}</h3>
                <p className="mt-1 text-sm text-muted">{card.description}</p>
              </div>
            </Panel>
          );
        })}
      </div>
    </PageShell>
  );
};

// Layout principal com sidebar sempre visível
const MainLayout = () => {
  const { user } = useAuth();
  
  return (
    <div className="main-layout">
      <Sidebar user={user} />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/boletim/producao-bpa" element={<ProducaoBPA />} />
          <Route path="/boletim/gera-bpa" element={<GeraBPA />} />
          <Route path="/boletim/pacientes-incompletos" element={<PacientesIncompletos />} />
          <Route path="/bpa/viagem" element={<BpaTransporte />} />
          <Route path="/boletim/bpa-transporte" element={<BpaTransporte />} />
          <Route path="/usuarios" element={<Usuarios />} />
          <Route path="/transport/reports" element={<TransportReports />} />
          <Route path="/transport/*" element={<Transport />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router basename="/adm_i9">
        <DocumentTitle />
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/painel-chamadas" element={<PainelChamadas />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
